import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

import { secureGet, secureDelete, securePost, secureGetPdf } from "../../config/api";
import { formatDateTime } from "../../utils/format";

import Filter from "../../components/Filter";
import Rows from "../../components/Rows";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import { PrimaryButton } from "../../components/PrimaryButton";
import ModuleWrapper from "../../components/ModuleWrapper";
import UserContext from "../../Shared/UserContext";

import PrintIcon from "../../icons/printerBlack.svg";
import DeleteIcon from "../../icons/trashR.svg";
import AddIcon from "../../icons/plusB.svg";
import { saveAs } from "file-saver";

const STATUS_COLORS = {
  SIGNED: "#06D6A0",
  CREATED: "#A8ADC3",
  QUOTE_GENERATED: "#3872B0",
};


const FichesReception = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const topRef = useRef(null);

  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    needle: "",
    clientId: null,
    vehicleId: null,
    status: null,
    dateRange: {},
  });

  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [statuses] = useState([
    { value: "SIGNED", label: "Signée" },
    { value: "QUOTE_GENERATED", label: "Devis généré" },
    { value: "CREATED", label: "Créée" },
  ]);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const { state: { role } } = useContext(UserContext);

  const [showFicheReceptionModule, setShowFicheReceptionModule] = useState(false);
  const showSnackbar = (message, severity = "success", manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const columns = [
    { label: "ID", key: "receptionNumber" },
    {
      label: "Date",
      key: "createdAt",
      render: (date) => formatDateTime(date),
    },
    {
      label: "Client",
      key: "clientName",
    },
    {
      label: "Marque",
      key: "vehicle",
      render: (vehicle) => (vehicle?.brand?.name ? vehicle?.brand?.name : "—"),
    },
    {
      label: "Modèle",
      key: "vehicle",
      render: (vehicle) => (vehicle?.model ? vehicle?.model : "—"),
    },
    {
      label: "Couleur",
      key: "vehicle",
      render: (vehicle) => (vehicle?.color ? vehicle?.color : "—"),
    },
    {
      label: "Statut",
      key: "status",
      render: (status) => (
        <span
          className="text-white text-[12px] font-semibold px-2 py-1 rounded-full inline-block w-fit text-center"
          style={{ backgroundColor: STATUS_COLORS[status] || "#A8ADC3" }}
        >
          {t(`fr_status.${status}`, status).toUpperCase()}
        </span>
      ),
    },
/*     {
      key: "actions",
      render: (_, item) => (
        <div className="flex items-center justify-end gap-x-2">
           <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrint(item);
            }}
            className="w-8 h-8 flex items-center justify-center bg-[#FFFFFF] rounded-[12px] shadow-[0px_6px_26px_#626B7D12]"
            title="Imprimer"
          >
            <img src={PrintIcon} alt="print" className="w-5 h-5" />
          </button>
          {item?.status !== "SIGNED" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(item);
            }}
            className="w-8 h-8 flex items-center justify-center bg-[#FFFFFF] rounded-[12px] shadow-[0px_6px_26px_#626B7D12]"
            title="Supprimer"
          >
            <img src={DeleteIcon} alt="delete" className="w-5 h-5" />
          </button>
          )}
        </div>
      ),
    }, */
  ];

  const filterOptions = [
    { name: "needle", type: "search" },
    { name: "dateRange", type: "daterange" },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: clients.map((c) => ({
        value: c.id,
        reference: c.code,
        name: c.name,
      })),
    },
    {
      name: "vehicleId",
      label: "Véhicule",
      type: "dropdown",
      options: vehicles.map((v) => ({
        value: v.id,
        label: `${v.type || ''} - ${v.brandMake || ''} (${v.immatriculationNumber || ''})`,
        reference: `${v.immatriculationNumber} - ${v.color || ''}`,
        name: `${t(`filters.vehicles.${v.type}`)} - ${v.brand?.name || ''} - ${v.model?.name || ''} - ${v.year || ''}`.trim(),
      })),
    },
    {
      name: "status",
      label: "Statut",
      type: "dropdown",
      options: [...statuses],
    },
  ];

  // dropdowns
  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchDropdowns = async () => {
      try {
        const clientsRes = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientsRes.data || []);

        if (filters?.clientId && filters?.clientId !== null) {
          const vehiclesRes = await secureGet(`/vehicles/client/${filters?.clientId}`, showSnackbar, { signal });
          console.log("Vehicles fetched:", vehiclesRes.data);
          setVehicles(vehiclesRes.data || []);
        }
      } catch (e) {
          if (e.name !== "AbortError") console.error(e);
        }
      };

      fetchDropdowns();
      return () => abortController.abort();
    }, [filters?.clientId]);

  // paged data
  const fetchPaged = useCallback(
    async (page = 0, size = pageSize, activeFilters = filters, signal) => {
      setLoading(true);

      try {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("size", size);
        if (activeFilters.needle) params.append("needle", activeFilters.needle);
        if (activeFilters.clientId) params.append("clientId", activeFilters.clientId);
        if (activeFilters.vehicleId) params.append("vehicleId", activeFilters.vehicleId);
        if (activeFilters.status) params.append("status", activeFilters.status);
        if (activeFilters.dateRange?.startDate) params.append("startDate", activeFilters.dateRange.startDate);
        if (activeFilters.dateRange?.endDate) params.append("endDate", activeFilters.dateRange.endDate);
        const res = await secureGet(`/reception-documents?${params.toString()}`, showSnackbar, {
          signal,
        });
        const { content = [], totalPages = 1 } = res.data || {};
        console.log("Fiches reception fetched:", content);
        setCurrentPageData(content);
        setTotalPages(totalPages);
      } catch (e) {
        if (e.name !== "AbortError") console.error("Error fetching fiches:", e);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize]
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);
      fetchPaged(restoredPage, restoredSize, restoredFilters, signal);
    } else {
      fetchPaged(0, pageSize, filters, signal);
    }

    return () => abortController.abort();
  }, []); // eslint-disable-line

  // pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaged(page, pageSize, filters);
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaged(0, size, filters);
  };

  // filter handlers
  const handleFilterChange = (filterName, filterValue) => {
    const updated = { ...filters };

    if (filterName === "dateRange") {
      updated.dateRange = {
        startDate: filterValue?.startDate || null,
        endDate: filterValue?.endDate || null,
      };
    } else if (filterName === "status") {
      updated.status = filterValue;
    } else {
      updated[filterName] = filterValue;
    }

    setFilters(updated);
    setCurrentPage(0);
    fetchPaged(0, pageSize, updated);
  };

  const resetFilters = () => {
    const def = { needle: "", clientId: null, vehicleId: null, status: null, dateRange: {} };
    setFilters(def);
    setVehicles([]);
    setCurrentPage(0);
    fetchPaged(0, pageSize, def);
  };

  // actions
  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await secureDelete(`reception-documents/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaged(currentPage, pageSize, filters);
      showSnackbar(t("confirmation.deleted"), "success");
    } catch (e) {
      console.error("Delete error:", e);
      setDeletePopupVisible(false);
    }
  };

  const handlePrint = async (item) => {
    try {
      const blob = await secureGetPdf(`fiche-reception/${item.id}/print`, showSnackbar);
      saveAs(blob, `fiche-reception-${item.id}.pdf`);
      showSnackbar(t("confirmation.print_started"), "success");
    } catch (e) {
      console.error("Print error:", e);
      showSnackbar(t("errors.print_failed"), "error");
    }
  };

  const handleRowClick = (item) => {
    history.push({
      pathname: `/fiche-reception/details/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };

  const handleFicheReceptionModuleClose = useCallback((action) => {
    if (action === "submit") {
      fetchPaged(0, pageSize, filters);
    }
    setShowFicheReceptionModule(false);
  }, []);

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox — {t("titles.fiche_reception")}</title>
      </Helmet>

      <div className="flex items-center mb-[15px]">
        <h1 className="text-[40px] font-semibold">{t("titles.fiche_reception")}</h1>
        {(role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_ADMIN") && (
        <div className="ml-auto flex gap-2 text-[16px]">
          <PrimaryButton
            label={t("primaryButtons.add_fiche_reception")}
            icon={AddIcon}
            onClick={() => {
              setShowFicheReceptionModule(true);
            }}
          />
        </div>
        )}
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div style={{ position: "relative", minHeight: 200 }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}

        <Rows columns={columns} data={currentPageData} onRowClick={handleRowClick} />
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showFicheReceptionModule && (
        <ModuleWrapper
          moduleType="reception-documents"
          mode="add"
          onClose={handleFicheReceptionModuleClose}
          onSuccess={(msg) => showSnackbar(msg, "success")}
          initialValues={{}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default FichesReception;
